import { useState, useCallback, useEffect } from "react";
import { atom, useRecoilValue } from "recoil";
import { IReview } from "src/domains";
import {
  queryGetReviewsByUserAsUser,
  queryGetReviewsByUserAsServiceOwner,
  queryGetReviewsByService,
  queryGetReviewsByServiceToUser,
  mutationConversationReview,
  IMutationConversationReviewRequest,
} from "src/graphql";
import { AppState, AppStateStatus } from "react-native";
import {
  createReviewEmit,
  CreateReviewEvent,
  TabChangeEvent,
} from "src/utils/event";
import { EventRegister } from "react-native-event-listeners";
import { useAuth, AuthState } from "src/hooks/cognito";

export const byUserAsUserReviewState = atom<IReview[]>({
  key: "byUserAsUserReviewState",
  default: [],
});

export const byUserAsServiceOwnerReviewState = atom<IReview[]>({
  key: "byUserAsServiceOwnerReviewState",
  default: [],
});

export const useReviewByUserAsUser = (userId?: string) => {
  const [reviews, setReviews] = useState<IReview[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  let _isMount = true;

  const refresh = useCallback(async () => {
    if (!userId || !_isMount) {
      return;
    }
    if (_isMount) {
      setLoading(true);
    }
    try {
      const response = await queryGetReviewsByUserAsUser({
        userId,
      });
      if (_isMount) {
        setReviews(response.data.getReviewsByUserAsUser?.items || []);
      }
    } catch (e) {
      console.error(e);
      if (_isMount) {
        setError(e as Error);
      }
    }
    if (_isMount) {
      setLoading(false);
    }
  }, [setReviews, userId]);

  useEffect(() => {
    const createReviewListener = EventRegister.addEventListener(
      CreateReviewEvent,
      () => refresh()
    );
    const tabChangeListener = EventRegister.addEventListener(
      TabChangeEvent,
      (e) => {
        if (e.tabName === "MyPage") {
          refresh();
        }
      }
    );
    return () => {
      EventRegister.removeEventListener(createReviewListener as string);
      EventRegister.removeEventListener(tabChangeListener as string);
    };
  }, [refresh]);

  const onAppStateChange = useCallback(
    (state: AppStateStatus) => {
      if (state === "active") {
        refresh();
      }
    },
    [refresh]
  );

  useEffect(() => {
    AppState.addEventListener("change", onAppStateChange);
    // TODO: Check if it works corrent (muliti hundler)
    // return () => {
    //   AppState.removeEventListener('change', onAppStateChange);
    // };
  }, [onAppStateChange]);

  useEffect(() => {
    refresh();
  }, [refresh, userId]);

  useEffect(() => {
    _isMount = true;
    return () => {
      _isMount = false;
    };
  }, []);

  return [reviews, loading, error, refresh] as const;
};

export const useReviewByUserAsServiceOwner = (userId?: string) => {
  const [reviews, setReviews] = useState<IReview[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  let _isMount = true;

  const refresh = useCallback(async () => {
    if (!userId || !_isMount) {
      return;
    }
    if (_isMount) {
      setLoading(true);
    }
    try {
      const response = await queryGetReviewsByUserAsServiceOwner({
        userId,
      });
      if (_isMount) {
        setReviews(response.data.getReviewsByUserAsServiceOwner?.items || []);
      }
    } catch (e) {
      console.error(e);
      if (_isMount) {
        setError(e as Error);
      }
    }
    if (_isMount) {
      setLoading(false);
    }
  }, [setReviews, userId]);

  useEffect(() => {
    const createReviewListener = EventRegister.addEventListener(
      CreateReviewEvent,
      () => refresh()
    );
    const tabChangeListener = EventRegister.addEventListener(
      TabChangeEvent,
      (e) => {
        if (e.tabName === "MyPage") {
          refresh();
        }
      }
    );
    return () => {
      EventRegister.removeEventListener(createReviewListener as string);
      EventRegister.removeEventListener(tabChangeListener as string);
    };
  }, [refresh]);

  const onAppStateChange = useCallback(
    (state: AppStateStatus) => {
      if (state === "active") {
        refresh();
      }
    },
    [refresh]
  );

  useEffect(() => {
    AppState.addEventListener("change", onAppStateChange);
    // TODO: Check if it works corrent (muliti hundler)
    // return () => {
    //   AppState.removeEventListener('change', onAppStateChange);
    // };
  }, [onAppStateChange]);

  useEffect(() => {
    refresh();
  }, [refresh, userId]);

  useEffect(() => {
    _isMount = true;
    return () => {
      _isMount = false;
    };
  }, []);

  return [reviews, loading, error, refresh] as const;
};

export const useReviewsByOffer = (offerId?: string | undefined) => {
  const auth = useAuth();
  const [reviews, setReviews] = useState<IReview[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  let _isMount = true;

  const refresh = useCallback(async () => {
    if (!offerId || !_isMount) {
      return;
    }
    if (_isMount) {
      setLoading(true);
    }
    if (_isMount) {
      setError(undefined);
    }
    try {
      const response = await queryGetReviewsByService(
        {
          serviceId: offerId,
          limit: 5,
        },
        auth === AuthState.Auth ? "AMAZON_COGNITO_USER_POOLS" : "API_KEY"
      );
      if (_isMount) {
        setReviews(response?.data.getReviewsByService?.items || []);
      }
    } catch (e) {
      console.error(e);
      if (_isMount) {
        setError(e as Error);
      }
    }
    if (_isMount) {
      setLoading(false);
    }
  }, [offerId, auth]);

  useEffect(() => {
    const createReviewListener = EventRegister.addEventListener(
      CreateReviewEvent,
      () => refresh()
    );
    return () => {
      EventRegister.removeEventListener(createReviewListener as string);
    };
  }, [refresh]);

  useEffect(() => {
    refresh();
  }, [offerId, auth, refresh]);

  useEffect(() => {
    _isMount = true;
    return () => {
      _isMount = false;
    };
  }, []);

  return [reviews, loading, error, refresh] as const;
};

export const useReviewsByOfferToUser = (offerId?: string | undefined) => {
  const [reviews, setReviews] = useState<IReview[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  let _isMount = true;

  const refresh = useCallback(async () => {
    if (!offerId || !_isMount) {
      return;
    }
    if (_isMount) {
      setLoading(true);
    }
    if (_isMount) {
      setError(undefined);
    }
    try {
      const response = await queryGetReviewsByServiceToUser({
        serviceId: offerId,
        limit: 5,
      });
      if (_isMount) {
        setReviews(response?.data.getReviewsByServiceToUser?.items || []);
      }
    } catch (e) {
      console.error(e);
      if (_isMount) {
        setError(e as Error);
      }
    }
    if (_isMount) {
      setLoading(false);
    }
  }, [offerId]);

  useEffect(() => {
    const createReviewListener = EventRegister.addEventListener(
      CreateReviewEvent,
      () => refresh()
    );
    return () => {
      EventRegister.removeEventListener(createReviewListener as string);
    };
  }, [refresh]);

  useEffect(() => {
    refresh();
  }, [offerId, refresh]);

  useEffect(() => {
    _isMount = true;
    return () => {
      _isMount = false;
    };
  }, []);

  return [reviews, loading, error, refresh] as const;
};

export const useReviewActions = () => {
  const createReview = async (params: IMutationConversationReviewRequest) => {
    await mutationConversationReview(params);
    createReviewEmit();
  };

  return {
    createReview,
  } as const;
};
